'use client'

import ReactPlayerLoader from '@brightcove/react-player-loader'
import { useState } from 'react'

import { cn } from '@src/utils/tailwind'

export interface BrightcovePlayerProps {
  videoId: string
  accountId?: string
  playerId?: string
  videoPlayerAspectRatio?: string
  autoplay?: boolean
  fluid?: boolean
  isMuted?: boolean
  width?: number
}

const UnmuteIcon = () => (
  <svg
    className="size-10 md:size-14 lg:size-20"
    width="81"
    height="82"
    viewBox="0 0 81 82"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect y="0.556641" width="81" height="81" rx="40.5" fill="white" />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M51.538 45.8159V22.1102C51.538 21.2862 51.2619 20.5817 50.7099 19.9967C50.1578 19.4117 49.4615 19.1191 48.6211 19.1191C48.0443 19.1191 47.5252 19.2551 47.0638 19.527C46.6024 19.7989 46.0585 20.2233 45.4323 20.8L36.212 29.3529C36.0802 29.4683 35.9566 29.5259 35.8412 29.5259H35.2233L51.538 45.8159ZM60.956 63.6138C61.4669 63.6138 61.8953 63.4325 62.2414 63.0699C62.6039 62.7074 62.7852 62.2748 62.7852 61.7722C62.7852 61.2696 62.6039 60.837 62.2414 60.4744L21.7018 19.9349C21.3558 19.5888 20.9232 19.4158 20.4041 19.4158C19.885 19.4158 19.4442 19.5888 19.0816 19.9349C18.7355 20.2974 18.5625 20.7383 18.5625 21.2574C18.5625 21.7765 18.7355 22.2008 19.0816 22.5304L59.6459 63.0699C60.0084 63.4325 60.4451 63.6138 60.956 63.6138ZM48.6705 60.7216C49.2968 60.7216 49.8447 60.5568 50.3144 60.2272C50.784 59.8976 51.126 59.395 51.3402 58.7194L24.3715 31.7754C23.6299 32.6158 23.2592 33.86 23.2592 35.508V44.4069C23.2592 46.1702 23.6753 47.4845 24.5075 48.3496C25.3397 49.2148 26.588 49.6474 28.2524 49.6474H34.8772C35.0914 49.6474 35.2809 49.7216 35.4457 49.8699L45.4323 59.189C46.0091 59.7328 46.5488 60.1242 47.0514 60.3632C47.554 60.6021 48.0938 60.7216 48.6705 60.7216Z"
      fill="#161819"
    />
  </svg>
)

export const BrightcovePlayerAutoplay = ({
  videoId,
  accountId = '1634697872',
  playerId = 'AFPbTgXrMJ', // https://studio.brightcove.com/products/videocloud/players/AFPbTgXrMJ
  videoPlayerAspectRatio = '16 / 9',
  isMuted = true,
  width,
}: BrightcovePlayerProps) => {
  const [showUnmuteButton, setShowUnmuteButton] = useState(isMuted)

  return (
    <div className="relative size-full">
      <ReactPlayerLoader
        accountId={accountId}
        playerId={playerId}
        videoId={videoId}
        options={{ autoplay: true, muted: showUnmuteButton }}
        onSuccess={({ ref }: any) => {
          Object.assign(ref.el_.style, {
            borderRadius: '30px',
            width: width ? `${width}px` : '100%',
            aspectRatio: videoPlayerAspectRatio,
            height: 'auto',
          })
          Object.assign(ref.el_.parentElement.style, {
            width: '100%',
          })
        }}
      />
      <div
        className={cn('absolute top-0 left-0 size-full', {
          hidden: !showUnmuteButton,
        })}
      >
        <button
          className={cn('absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 ')}
          onClick={() => {
            setShowUnmuteButton(false)
          }}
        >
          <UnmuteIcon />
        </button>
      </div>
    </div>
  )
}

export const BrightcovePlayerManualPlay = ({
  videoId,
  accountId = '1634697872',
  playerId = 'adCNPdiKU', // https://studio.brightcove.com/products/videocloud/players/adCNPdiKU
  videoPlayerAspectRatio = '16 / 9',
  width,
}: BrightcovePlayerProps) => {
  return (
    <ReactPlayerLoader
      accountId={accountId}
      playerId={playerId}
      videoId={videoId}
      options={{ autoplay: false }}
      onSuccess={({ ref }: any) => {
        Object.assign(ref.el_.style, {
          borderRadius: '30px',
          width: width ? `${width}px` : '100%',
          aspectRatio: videoPlayerAspectRatio,
          height: 'auto',
        })
        Object.assign(ref.el_.parentElement.style, {
          width: '100%',
        })
      }}
    />
  )
}

export const BrightcovePlayer = (props: BrightcovePlayerProps) => {
  return props?.autoplay ? (
    <BrightcovePlayerAutoplay {...props} />
  ) : (
    <BrightcovePlayerManualPlay {...props} />
  )
}
